/**
 * A link placeholder (`‹ViewSonic PX700HDH›`, shared/src/utils/link-tokens.ts)
 * is one unit in the search field, like a chip in a rich editor, though the
 * field is a plain textarea:
 *
 *  - a click or tap on it selects the whole placeholder, so it can be dragged
 *    to another place in the sentence, or cut and pasted (the system's Cut /
 *    Copy menu appears on a phone);
 *  - an edit that cuts into it (Backspace or Delete next to it, on any
 *    keyboard) takes all of it out (repairDamagedTokens);
 *  - the arrow keys step over it, the caret never sits inside it;
 *  - a selection that starts or ends inside one grows to take all of it.
 *
 * Pure: positions in, positions out. Nothing changes unless a placeholder is
 * in the text, so ordinary typing is untouched.
 */

import type { LinkTokenRange } from 'shared/src/utils/link-tokens';

export interface TokenEdit {
  text: string;
  /** Where the caret goes. */
  caret: number;
}

/** An arrow key at a placeholder's edge steps over it. The new caret, or null for the browser's own move. */
export function tokenArrow(
  key: 'ArrowLeft' | 'ArrowRight',
  start: number,
  end: number,
  ranges: readonly LinkTokenRange[],
): number | null {
  if (start !== end) return null;
  if (key === 'ArrowLeft') return ranges.find((r) => r.end === start)?.start ?? null;
  return ranges.find((r) => r.start === start)?.end ?? null;
}

/**
 * Where the selection should be so no placeholder is cut in two: a caret
 * inside one selects all of it; a range with an end inside one grows outward.
 * Null when nothing needs to move.
 */
export function snapSelection(
  start: number,
  end: number,
  ranges: readonly LinkTokenRange[],
): [number, number] | null {
  let from = start;
  let to = end;
  for (const range of ranges) {
    if (start === end) {
      if (start > range.start && start < range.end) {
        from = range.start;
        to = range.end;
      }
    } else {
      if (from > range.start && from < range.end) from = range.start;
      if (to > range.start && to < range.end) to = range.end;
    }
  }
  return from === start && to === end ? null : [from, to];
}

/**
 * An edit that cut into a placeholder (a soft keyboard's backspace, a
 * selection typed over, a partial cut) takes all of it out instead of leaving
 * `‹ViewSonic PX7` behind. Works on the before and after text, so it holds for
 * every way text can change. An edit that removes a placeholder whole (a
 * drag, a cut of the selected unit) or leaves it whole is not touched.
 * Null when no placeholder was damaged.
 */
export function repairDamagedTokens(
  prev: string,
  next: string,
  ranges: readonly LinkTokenRange[],
): TokenEdit | null {
  if (ranges.length === 0 || prev === next) return null;
  const max = Math.min(prev.length, next.length);
  let head = 0;
  while (head < max && prev[head] === next[head]) head++;
  let tail = 0;
  while (tail < max - head && prev[prev.length - 1 - tail] === next[next.length - 1 - tail]) tail++;
  const spanStart = head;
  const spanEnd = prev.length - tail;
  const inserted = next.slice(head, next.length - tail);

  const damaged = ranges.filter((r) => {
    if (spanStart === spanEnd) return spanStart > r.start && spanStart < r.end; // typed inside one
    const touches = spanStart < r.end && spanEnd > r.start;
    const covers = spanStart <= r.start && spanEnd >= r.end;
    return touches && !covers;
  });
  if (damaged.length === 0) return null;

  const lo = Math.min(spanStart, ...damaged.map((r) => r.start));
  const hi = Math.max(spanEnd, ...damaged.map((r) => r.end));
  const before = prev.slice(0, lo);
  let after = prev.slice(hi);
  // Nothing typed in its place: do not leave two spaces where it was.
  if (inserted === '' && /\s$/.test(before) && /^\s/.test(after)) after = after.replace(/^\s/, '');
  return { text: before + inserted + after, caret: lo + inserted.length };
}
